import ks, { kObject, kObjectArray } from "@shared/kalmstromShared4";
import { kEditableTable, kHTMLEditableColType } from "@shared/kEditableTable";
import { IListDefinition, IFieldDefinition } from "./DBInterfaces";
import DBState from "./DBState";
import DBModalManager from "./DBModalManager";
import DBStringUtils from "./DBStringUtils";
import DBFieldManager from "./DBFieldManager";
import DBDataGrid from "./DBDataGrid";

// ---------------------------------------------------------------------------
// Data model for CSV preview grid
// ---------------------------------------------------------------------------

class PreviewRow extends kObject {
    constructor() { super(); }
}

class PreviewRowCollection extends kObjectArray {
    public Items: PreviewRow[] = [];
}

// ---------------------------------------------------------------------------

export default class DBCSVImport {

    public static PendingCSVData: { Lines: string[]; List: IListDefinition } | null = null;
    private static PreviewGrid: kEditableTable | null = null;

    public static ImportCSV = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBState.SelectedListId === null) {
                DBModalManager.ShowToast("Please select a list first", "error");
                return;
            }
            DBState.DataModalListId = DBState.SelectedListId;
            const fileInput: HTMLElement | null = document.getElementById("csvFileInput");
            if (fileInput !== null) { fileInput.click(); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ImportCSV");
        }
    };

    public static HandleCSVFile = (event: Event): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const input: HTMLInputElement = event.target as HTMLInputElement;
            if (input.files === null || input.files.length === 0) { return; }
            const file: File = input.files[0];

            const reader: FileReader = new FileReader();
            const HandleLoad = (e: ProgressEvent<FileReader>): void => {
                if (e.target !== null && e.target.result !== null) {
                    DBCSVImport.ShowCSVPreview(e.target.result as string);
                }
            };
            reader.onload = HandleLoad;
            reader.readAsText(file);
            input.value = "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.HandleCSVFile");
        }
    };

    public static ShowCSVPreview = (csvText: string): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const list: IListDefinition | null = DBState.FindList(DBState.DataModalListId || "");
            if (list === null) { return; }

            const FilterNonEmpty = (line: string): boolean => { return line.trim() !== ""; };
            const lines: string[] = csvText.split("\n").filter(FilterNonEmpty);
            if (lines.length === 0) {
                DBModalManager.ShowToast("CSV file is empty", "error");
                return;
            }

            DBCSVImport.PendingCSVData = { Lines: lines, List: list };

            const listNameEl: HTMLElement | null = document.getElementById("csvPreviewListName");
            const rowCountEl: HTMLElement | null = document.getElementById("csvPreviewRowCount");
            if (listNameEl !== null) { listNameEl.textContent = list.Title; }
            if (rowCountEl !== null) { rowCountEl.textContent = String(lines.length - 1); }

            DBCSVImport.UpdateCSVPreview(true);
            DBModalManager.OpenModal("csvPreviewModal");

            DBCSVImport.SetupHeaderCheckbox();
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ShowCSVPreview");
        }
    };

    private static SetupHeaderCheckbox = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const headerCheckbox: HTMLInputElement | null = document.getElementById("csvHasHeaderCheckbox") as HTMLInputElement;
            if (headerCheckbox === null) { return; }
            headerCheckbox.checked = true;
            const HandleChange = (): void => {
                DBCSVImport.UpdateCSVPreview(headerCheckbox.checked);
            };
            headerCheckbox.onchange = HandleChange;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.SetupHeaderCheckbox");
        }
    };

    public static UpdateCSVPreview = (hasHeader: boolean): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBCSVImport.PendingCSVData === null) { return; }
            const lines: string[] = DBCSVImport.PendingCSVData.Lines;
            const list: IListDefinition = DBCSVImport.PendingCSVData.List;

            const firstLine: string[] = DBCSVImport.ParseCSVLine(lines[0]);
            const csvColumns: string[] = DBCSVImport.GetCsvColumnNames(firstLine, hasHeader);

            DBCSVImport.UpdatePreviewCounts(csvColumns.length, hasHeader, lines.length);
            DBCSVImport.RenderColumnMapping(csvColumns, list);
            DBCSVImport.RenderPreviewTable(csvColumns, lines, hasHeader);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.UpdateCSVPreview");
        }
    };

    private static GetCsvColumnNames = (firstLine: string[], hasHeader: boolean): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            if (hasHeader) { return firstLine; }
            const names: string[] = [];
            for (let i: number = 0; i < firstLine.length; i++) {
                names.push("Column " + (i + 1));
            }
            return names;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.GetCsvColumnNames");
            return [];
        }
    };

    private static UpdatePreviewCounts = (colCount: number, hasHeader: boolean, lineCount: number): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const colCountEl: HTMLElement | null = document.getElementById("csvPreviewColCount");
            const rowCountEl: HTMLElement | null = document.getElementById("csvPreviewRowCount");
            if (colCountEl !== null) { colCountEl.textContent = String(colCount); }
            if (rowCountEl !== null) { rowCountEl.textContent = String(hasHeader ? lineCount - 1 : lineCount); }
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.UpdatePreviewCounts");
        }
    };

    private static RenderColumnMapping = (csvColumns: string[], list: IListDefinition): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const mappingContainer: HTMLElement | null = document.getElementById("columnMappingContainer");
            if (mappingContainer === null) { return; }

            const listFields: { internalName: string; displayName: string }[] = DBCSVImport.BuildListFields(list);
            let html: string = "";

            const BuildMapping = (csvCol: string, index: number): void => {
                const autoMatch: string = DBCSVImport.FindAutoMatch(csvCol, listFields);
                html += DBCSVImport.BuildMappingRowHtml(csvCol, index, listFields, autoMatch);
            };
            csvColumns.forEach(BuildMapping);
            mappingContainer.innerHTML = html;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.RenderColumnMapping");
        }
    };

    private static BuildListFields = (list: IListDefinition): { internalName: string; displayName: string }[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const fields: { internalName: string; displayName: string }[] = [];
            fields.push({ internalName: "Title", displayName: "Title" });
            const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            const AddField = (entry: [string, IFieldDefinition]): void => {
                fields.push({ internalName: entry[0], displayName: entry[1].Title || entry[0] });
            };
            entries.forEach(AddField);
            return fields;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.BuildListFields");
            return [];
        }
    };

    private static FindAutoMatch = (csvCol: string, listFields: { internalName: string; displayName: string }[]): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            const lowerCsv: string = csvCol.toLowerCase();
            const IsMatch = (f: { internalName: string; displayName: string }): boolean => {
                return f.displayName.toLowerCase() === lowerCsv || f.internalName.toLowerCase() === lowerCsv;
            };
            const match: { internalName: string; displayName: string } | undefined = listFields.find(IsMatch);
            if (match !== undefined) { return match.internalName; }
            return "";
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.FindAutoMatch");
            return "";
        }
    };

    private static BuildMappingRowHtml = (csvCol: string, index: number, listFields: { internalName: string; displayName: string }[], autoMatch: string): string => {
        if (ks.kErrorMode === true) { return ""; }
        try {
            let optionsHtml: string = '<option value="">-- Skip Column --</option>';
            const AddOption = (field: { internalName: string; displayName: string }): void => {
                const selected: string = autoMatch === field.internalName ? " selected" : "";
                optionsHtml += '<option value="' + DBStringUtils.EscapeHtml(field.internalName) + '"' + selected + '>' + DBStringUtils.EscapeHtml(field.displayName) + '</option>';
            };
            listFields.forEach(AddOption);

            return '<div class="column-mapping-row">'
                + '<div><div style="font-size: 11px; color: var(--text-secondary); margin-bottom: 4px;">CSV Column</div>'
                + '<div style="font-weight: 500;">' + DBStringUtils.EscapeHtml(csvCol) + '</div></div>'
                + '<div class="column-mapping-arrow">\u2192</div>'
                + '<div><div style="font-size: 11px; color: var(--text-secondary); margin-bottom: 4px;">Maps to Field</div>'
                + '<select class="column-mapping-select" data-csv-index="' + index + '">' + optionsHtml + '</select></div></div>';
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.BuildMappingRowHtml");
            return "";
        }
    };

    private static RenderPreviewTable = (csvColumns: string[], lines: string[], hasHeader: boolean): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const container: HTMLElement | null = document.getElementById("csvPreviewTableContainer");
            if (container === null) { return; }

            DBCSVImport.PreviewGrid = new kEditableTable();
            DBCSVImport.PreviewGrid.AddBlankRows = false;

            for (let i: number = 0; i < csvColumns.length; i++) {
                const colKey: string = "col_" + i;
                const col = DBCSVImport.PreviewGrid.AddColumn(csvColumns[i], kHTMLEditableColType.Text, 160, colKey, "");
                col.Locked = true;
            }

            const dataLines: string[] = hasHeader ? lines.slice(1, 6) : lines.slice(0, 5);
            const collection: PreviewRowCollection = new PreviewRowCollection();
            for (const line of dataLines) {
                const values: string[] = DBCSVImport.ParseCSVLine(line);
                const row: PreviewRow = new PreviewRow();
                for (let i: number = 0; i < csvColumns.length; i++) {
                    (row as any)["col_" + i] = values[i] || "";
                }
                collection.push(row);
            }

            DBCSVImport.PreviewGrid.RowValues = collection;
            container.innerHTML = "";
            container.appendChild(DBCSVImport.PreviewGrid.ToTable());
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.RenderPreviewTable");
        }
    };

    public static ConfirmCSVImport = (): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            if (DBCSVImport.PendingCSVData === null) { return; }
            const lines: string[] = DBCSVImport.PendingCSVData.Lines;
            const list: IListDefinition = DBCSVImport.PendingCSVData.List;
            const hasHeader: boolean = (document.getElementById("csvHasHeaderCheckbox") as HTMLInputElement).checked;

            const columnMappings: { csvIndex: number; fieldName: string }[] = DBCSVImport.GetColumnMappings();
            if (columnMappings.length === 0) {
                DBModalManager.ShowToast("Please map at least one column", "error");
                return;
            }

            const columnDefs: any[] = DBCSVImport.BuildColumnDefs(list);
            const rowCount: number = hasHeader ? lines.length - 1 : lines.length;
            DBModalManager.ShowLoading("Importing " + rowCount + " rows...", true);
            DBModalManager.CloseModal("csvPreviewModal");

            const DoImport = (): void => {
                DBCSVImport.ProcessImportRows(lines, list, hasHeader, columnMappings, columnDefs);
            };
            setTimeout(DoImport, 100);
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ConfirmCSVImport");
        }
    };

    private static GetColumnMappings = (): { csvIndex: number; fieldName: string }[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const selects: NodeListOf<Element> = document.querySelectorAll(".column-mapping-select");
            const mappings: { csvIndex: number; fieldName: string }[] = [];
            const ProcessSelect = (select: Element): void => {
                const el: HTMLSelectElement = select as HTMLSelectElement;
                if (el.value) {
                    mappings.push({ csvIndex: parseInt(el.dataset.csvIndex || "0"), fieldName: el.value });
                }
            };
            selects.forEach(ProcessSelect);
            return mappings;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.GetColumnMappings");
            return [];
        }
    };

    private static BuildColumnDefs = (list: IListDefinition): any[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const defs: any[] = [{ internalName: "Title", displayName: "Title", type: "string" }];
            const entries: [string, IFieldDefinition][] = Object.entries(list.Properties || {}) as [string, IFieldDefinition][];
            const AddDef = (entry: [string, IFieldDefinition]): void => {
                const field: IFieldDefinition = entry[1];
                defs.push({
                    internalName: entry[0],
                    displayName: field.Title || entry[0],
                    type: field.Type,
                    format: field.Format,
                    required: field.Required,
                    minLength: field.MinLength,
                    maxLength: field.MaxLength
                });
            };
            entries.forEach(AddDef);
            return defs;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.BuildColumnDefs");
            return [];
        }
    };

    private static ProcessImportRows = (lines: string[], list: IListDefinition, hasHeader: boolean, columnMappings: { csvIndex: number; fieldName: string }[], columnDefs: any[]): void => {
        if (ks.kErrorMode === true) { return; }
        try {
            const dataLines: string[] = hasHeader ? lines.slice(1) : lines;
            let validRows: number = 0;
            let invalidRows: number = 0;

            const ProcessLine = (line: string, lineIndex: number): void => {
                const result: boolean = DBCSVImport.ProcessSingleImportRow(line, list, columnMappings, columnDefs);
                if (result) { validRows++; } else { invalidRows++; }

                if (lineIndex % 10 === 0 || lineIndex === dataLines.length - 1) {
                    const progress: number = ((lineIndex + 1) / dataLines.length) * 100;
                    DBModalManager.UpdateLoadingProgress(progress);
                }
            };
            dataLines.forEach(ProcessLine);

            DBDataGrid.RenderDataGrid(list);
            DBModalManager.HideLoading();

            if (invalidRows > 0) {
                DBModalManager.ShowToast("Imported " + validRows + " row(s), skipped " + invalidRows + " invalid row(s)", "success");
            } else {
                DBModalManager.ShowToast("Successfully imported " + validRows + " row(s)", "success");
            }

            DBCSVImport.PendingCSVData = null;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ProcessImportRows");
        }
    };

    private static ProcessSingleImportRow = (line: string, list: IListDefinition, columnMappings: { csvIndex: number; fieldName: string }[], columnDefs: any[]): boolean => {
        if (ks.kErrorMode === true) { return false; }
        try {
            const values: string[] = DBCSVImport.ParseCSVLine(line);
            const newRow: any = {};
            let rowValid: boolean = true;

            const ProcessMapping = (mapping: { csvIndex: number; fieldName: string }): void => {
                const value: string = mapping.csvIndex < values.length ? values[mapping.csvIndex].trim() : "";
                const FindDef = (c: any): boolean => { return c.internalName === mapping.fieldName; };
                const fieldDef: any | undefined = columnDefs.find(FindDef);

                if (fieldDef !== undefined) {
                    const validation: any = DBFieldManager.ValidateCellValue(value, fieldDef);
                    if (!validation.valid) { rowValid = false; }
                    else { newRow[mapping.fieldName] = validation.value; }
                }
            };
            columnMappings.forEach(ProcessMapping);

            if (rowValid) { list.Data.push(newRow); }
            return rowValid;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ProcessSingleImportRow");
            return false;
        }
    };

    public static ParseCSVLine = (line: string): string[] => {
        if (ks.kErrorMode === true) { return []; }
        try {
            const result: string[] = [];
            let current: string = "";
            let inQuotes: boolean = false;
            let i: number = 0;

            while (i < line.length) {
                const char: string = line[i];
                const nextChar: string = i + 1 < line.length ? line[i + 1] : "";

                if (inQuotes) {
                    if (char === '"') {
                        if (nextChar === '"') { current += '"'; i += 2; continue; }
                        else { inQuotes = false; i++; continue; }
                    } else { current += char; i++; }
                } else {
                    if (char === '"') { inQuotes = true; i++; }
                    else if (char === ",") { result.push(current.trim()); current = ""; i++; }
                    else { current += char; i++; }
                }
            }

            result.push(current.trim());
            return result;
        } catch (e) {
            ks.kGlobalErrorHandler(e, "DBCSVImport.ParseCSVLine");
            return [];
        }
    };
}
